import 'isomorphic-fetch';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import ApiListingError from './ApiListingError';
import reportErrorToSentry from '~/utils/reportErrorToSentry';
import {
  buildFiltersParamsString,
  buildQueryParamsString,
  buildPageParamStringForSection,
  buildPageParamStringForSections,
  applyPathParamsToUrl,
} from '../common/utils';
import { hasGeolocationFilter } from '../../Serp/utils';

const END_POINTS = {
  SEARCH_ADS: '/listing/search/{vertical_name}',
  LANDING_ADS: '/listing/landing/{vertical_name}',
  RETENTION_ADS: '/listing/retention/{vertical_name}',
  AD: '/listing/{vertical_name}/{id}',
};

const FETCH_OPTIONS = {
  method: 'GET',
  mode: 'cors',
  headers: {
    Accept: 'application/vnd.api+json',
    'Content-Type': 'application/vnd.api+json',
  },
};

/* eslint-disable quote-props */
export const FILTERS = {
  region: true,
  city: true,
  city_area: true,
  geo_id: true,
  date_from: true,
  decrease_price: true,
  photos: true,
  parking: true,
  new_build: true,
  property_type: true,
  price_min: true,
  price_max: true,
  lat_min: true,
  lat_max: true,
  long_min: true,
  long_max: true,
  area_min: true,
  area_max: true,
  rooms_min: true,
  rooms_max: true,
  bathrooms_min: true,
  bathrooms_max: true,
  year_min: true,
  year_max: true,
  mileage_min: true,
  mileage_max: true,
  doors_min: true,
  doors_max: true,
  model: true,
  make: true,
  car_type: true,
  transmission: true,
  fuel: true,
  /* Jobs specific filters */
  salary_min: true,
  salary_max: true,
  category: true,
  company: true,
  job_type: true,
  working_hours: true,
  experience_min: true,
  telecommute: true,
};
/* eslint-enable */

class Api {
  static fetch(url) {
    let response = null;
    return fetch(url, FETCH_OPTIONS)
      .then(res => {
        response = res;
        return response.json();
      })
      .then(json => {
        if (json.errors) {
          const error = Array.isArray(json.errors) ? json.errors[0] : json.errors;
          const errorMessage = error.detail;
          throw new ApiListingError(error.status, errorMessage, response.status);
        }

        return json;
      })
      .catch(error => {
        reportErrorToSentry(error);
        throw error;
      });
  }

  constructor(urlBase) {
    this.urlBase = urlBase;
  }

  static updateGeoLocationFilters(filters, geoId) {
    if (!hasGeolocationFilter(filters) && geoId) {
      return {
        ...filters,
        city_area: undefined,
        city: undefined,
        region: undefined,
        geo_id: geoId,
      };
    }

    return { ...filters, geo_id: undefined };
  }

  static getParams(searchParams) {
    const origin = get(searchParams, 'origin');
    const isSemUrl = get(searchParams, 'isSemUrl');
    const boostAdsSince = get(searchParams, 'boost_ads_since');
    const sections = get(searchParams, 'sections');
    const filters = get(searchParams, 'filters');
    const sort = get(searchParams, 'sort');
    const page = get(searchParams, 'page');
    const userCampaignIds = get(searchParams, 'userCampaignIds');
    const geoId = get(searchParams, 'geoId');
    const queryParams = {
      what: get(searchParams, 'what_d'),
      type_id: get(searchParams, 'type_id'),
      country_code: get(searchParams, 'country_code'),
      device: 'mobile', // Force value
    };

    if (origin) {
      queryParams.origin_id = origin;
    }

    if (userCampaignIds) {
      queryParams.user_campaign_ids = userCampaignIds;
    }

    if (boostAdsSince) {
      queryParams.boost_ads_since = boostAdsSince;
    }

    return {
      isSemUrl,
      boostAdsSince,
      sections,
      filters: Api.updateGeoLocationFilters(filters, geoId),
      geoId,
      sort,
      page,
      queryParams,
    };
  }

  static computeParamsAsStringArray(params) {
    const queryParamsStr = params.queryParams ? buildQueryParamsString(params.queryParams) : '';
    const filterParamsStr = params.filters ? buildFiltersParamsString(params.filters) : '';
    const sortParamStr = params.sort ? `sort=${params.sort}` : '';
    const pageParamStr = params.page ? `page[number]=${params.page}` : '';
    const clientIdParamStr = 'client_id=pwa-mobile';

    return [queryParamsStr, filterParamsStr, sortParamStr, pageParamStr, clientIdParamStr];
  }

  searchAds(searchParams) {
    const resource = `${this.urlBase}${END_POINTS.SEARCH_ADS}`;
    const pathParams = {
      vertical_name: get(searchParams, 'vertical_name'),
    };

    const parameters = Api.getParams(searchParams);

    const sectionParamsStr = buildPageParamStringForSections(parameters.sections);
    const parametersStrArray = Api.computeParamsAsStringArray(parameters);
    parametersStrArray.push(sectionParamsStr);

    const params = parametersStrArray.filter(x => !isEmpty(x)).join('&');
    const resourceWithParams = applyPathParamsToUrl(resource, pathParams);
    const url = `${resourceWithParams}?${params}`;

    return Api.fetch(url);
  }

  retentionAds(searchParams) {
    const resource = `${this.urlBase}${END_POINTS.RETENTION_ADS}`;
    const pathParams = {
      vertical_name: get(searchParams, 'vertical_name'),
    };

    const parameters = Api.getParams(searchParams);
    const sectionParamsStr = buildPageParamStringForSection(parameters.sections, 'organic');
    const parametersStrArray = Api.computeParamsAsStringArray(parameters);
    parametersStrArray.push(sectionParamsStr);

    const params = parametersStrArray.filter(x => !isEmpty(x)).join('&');
    const resourceWithParams = applyPathParamsToUrl(resource, pathParams);
    const url = `${resourceWithParams}?${params}`;

    return Api.fetch(url);
  }

  landingAds(searchParams) {
    const resource = `${this.urlBase}${END_POINTS.LANDING_ADS}`;
    const pathParams = {
      vertical_name: get(searchParams, 'vertical_name'),
    };

    const parameters = Api.getParams(searchParams);
    const sectionParamsStr = buildPageParamStringForSection(parameters.sections, 'organic');
    const parametersStrArray = Api.computeParamsAsStringArray(parameters);
    parametersStrArray.push(sectionParamsStr);

    const params = parametersStrArray.filter(x => !isEmpty(x)).join('&');
    const resourceWithParams = applyPathParamsToUrl(resource, pathParams);
    const url = `${resourceWithParams}?${params}`;

    return Api.fetch(url);
  }

  /**
   * Returns information for the given ad
   *
   * @param {Object} search Search object
   * @param {string} search.id
   * @param {string} search.country_code
   * @param {string} search.verticalName
   * @param {string} search.device
   *
   * @returns {Object}
   *
   * @throws {ApiListingError}
   * @memberof Api
   */
  ad(search) {
    const resource = `${this.urlBase}${END_POINTS.AD}`;
    const pathParams = {
      id: search.id,
      vertical_name: search.verticalName,
    };
    const queryParams = {
      country_code: search.countryCode,
      device: 'mobile', // search.device,
    };

    const queryParamsStr = queryParams ? buildQueryParamsString(queryParams) : '';
    const resourceWithParams = applyPathParamsToUrl(resource, pathParams);
    const url = `${resourceWithParams}?${queryParamsStr}`;

    return Api.fetch(url);
  }
}

export default Api;



// WEBPACK FOOTER //
// ./app/sdk/listing/Api.js